import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CopyButton from "../components/ServicesStatus/ClipbaordButton";
import { Link } from "react-router-dom";
import { BASE_URL } from "../components/config/config";

export default function RazorPayment({
  tickectId,
  phonenumber,
  isPickDrop = false,
  amount,
  consumerId,
}) {
  const [ticketModalData, setTicketModalData] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const updatePaymentStatus = useCallback(
    async (transactionId, status) => {
      try {
        const endpoint = isPickDrop
          ? `${BASE_URL}payment/capture-razorpay-payment`
          : `${BASE_URL}payment/capture-payment-broadcastTicket`;

        const payload = {
          ticket_obj_id: tickectId,
          status: status,
          amount: isPickDrop ? amount : 99,
          userId: consumerId,
          transactionId: transactionId,
        };

        const res = await axios.post(endpoint, payload);

        if (res?.data?.success === 1) {
          setTicketModalData(res.data.data);
          setTicketModalOpen(true);
        } else {
          setError("Payment verification failed. Please contact support.");
        }
      } catch (err) {
        setError(
          "Failed to verify payment status. Please check your booking status later."
        );
        console.error("Status update error:", err);
      }
    },
    [amount, consumerId, isPickDrop, tickectId]
  );

  const handleInitiatePayment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${BASE_URL}center/get-razorpay-key`);

      const options = {
        key: res?.data?.data?.key_id,
        amount: isPickDrop ? amount * 100 : 99 * 100,
        currency: "INR",
        name: "OnIt Services",
        description: "Request Booking",
        image: "https://onit.services/static/media/main_logo.406f2fdc.png",
        handler: (response) => {
          if (response?.razorpay_payment_id?.trim()) {
            updatePaymentStatus(response.razorpay_payment_id, "success");
          } else {
            updatePaymentStatus(response.razorpay_payment_id, "failed");
          }
        },
        prefill: {
          name: "onit",
          email: "email.com",
          contact: phonenumber,
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError("Failed to initialize payment. Please try again.");
      console.error("Payment error:", err);
    } finally {
      setLoading(false);
    }
  }, [amount, isPickDrop, phonenumber, updatePaymentStatus]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      setLoading(false);
      handleInitiatePayment();
    };
    script.onerror = () => {
      setError(
        "Failed to load Razorpay. Please check your internet connection."
      );
      setLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [retryCount, handleInitiatePayment]);

  const handleRetry = () => {
    setError(null);
    setRetryCount((prev) => prev + 1);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Initializing payment gateway...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" color="primary" onClick={handleRetry}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Modal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        aria-labelledby="payment-success-modal"
        aria-describedby="payment-success-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "60%" },
            maxWidth: 700,
            bgcolor: "background.paper",
            border: "2px solid #eee",
            boxShadow: 24,
            borderRadius: 2,
            p: { xs: 2, md: 4 },
          }}
        >
          <Box textAlign="center" mb={3}>
            <VerifiedIcon color="success" fontSize="large" />
            <Typography variant="h6" component="h2" mt={1}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your request has been created successfully.
            </Typography>
          </Box>

          <List>
            <ListItem>
              <ListItemText primary="Ticket ID" />
              <Typography fontWeight="bold">
                {ticketModalData?.ticket_id}
              </Typography>
            </ListItem>
            <Divider />

            <ListItem>
              <ListItemText primary="Status" />
              <Typography fontWeight="bold" color="success.main">
                {ticketModalData?.ticket_status}
              </Typography>
            </ListItem>
            <Divider />

            <ListItem>
              <ListItemText primary="Created At" />
              <Typography>
                {new Date(ticketModalData?.createdAt).toLocaleString()}
              </Typography>
            </ListItem>
            <Divider />

            <ListItem>
              <ListItemText primary="Tracking Link" />
              <Box display="flex" alignItems="center">
                <CopyButton
                  webLink={`${window.location.origin}/ServiceStatus/?${ticketModalData?._id}`}
                />
                <Link
                  to={`/ServiceStatus/?${ticketModalData?._id}`}
                  style={{ marginLeft: 16 }}
                >
                  <Button size="small" variant="outlined">
                    View Status
                  </Button>
                </Link>
              </Box>
            </ListItem>
          </List>

          <Box mt={3} textAlign="center">
            <Button
              variant="contained"
              onClick={() => setTicketModalOpen(false)}
              fullWidth
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
